import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef } from "react";
import { useTheme } from "@/app/providers/ThemeContext";
import { handleLandingAnchorClick } from "@/landing/landingScroll";

gsap.registerPlugin(ScrollTrigger);

const candles = [
    { wickTop: 12, bodyH: 50, wickBot: 8, bull: true },
    { wickTop: 20, bodyH: 36, wickBot: 14, bull: false },
    { wickTop: 8, bodyH: 66, wickBot: 10, bull: true },
    { wickTop: 18, bodyH: 46, wickBot: 6, bull: true },
    { wickTop: 10, bodyH: 30, wickBot: 20, bull: false },
    { wickTop: 6, bodyH: 76, wickBot: 10, bull: true },
    { wickTop: 14, bodyH: 56, wickBot: 8, bull: true },
];

export default function CTA() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const sectionRef = useRef(null);
    const leftRef = useRef(null);
    const badgeRef = useRef(null);
    const headRef = useRef(null);
    const paraRef = useRef(null);
    const actsRef = useRef(null);
    const candlesRef = useRef(null);
    const candleEls = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {

            // ── Left: stagger badge → h → p → buttons ────────────────────────
            gsap.timeline({
                scrollTrigger: { trigger: leftRef.current, start: "top 82%", toggleActions: "play none none none" },
            })
                .fromTo(badgeRef.current,
                    { opacity: 0, y: 14, scale: 0.93 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: "power3.out" }
                )
                .fromTo(headRef.current,
                    { opacity: 0, y: 28 },
                    { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
                    "-=0.25"
                )
                .fromTo(paraRef.current,
                    { opacity: 0, y: 16 },
                    { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" },
                    "-=0.3"
                )
                .fromTo(actsRef.current,
                    { opacity: 0, y: 12 },
                    { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" },
                    "-=0.25"
                );

            // ── Candles: grow up from height 0, staggered ────────────────────
            gsap.timeline({
                scrollTrigger: { trigger: candlesRef.current, start: "top 84%", toggleActions: "play none none none" },
            })
                .fromTo(candlesRef.current,
                    { opacity: 0, scale: 0.9 },
                    { opacity: 1, scale: 1, duration: 0.45, ease: "power2.out" }
                )
                .fromTo(
                    candleEls.current.filter(Boolean),
                    { scaleY: 0, opacity: 0, transformOrigin: "bottom center" },
                    { scaleY: 1, opacity: 1, duration: 0.5, stagger: 0.07, ease: "power3.out" },
                    "-=0.2"
                );

            // ── Candle wicks: subtle flicker loop ────────────────────────────
            candleEls.current.filter(Boolean).forEach((el, i) => {
                gsap.to(el.querySelectorAll(".wick"), {
                    scaleY: 0.7, transformOrigin: "center center",
                    duration: 0.6 + i * 0.1,
                    repeat: -1, yoyo: true,
                    ease: "sine.inOut",
                    delay: i * 0.15,
                });
            });

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <div
            id="cta"
            ref={sectionRef}
            style={{
                background: isDark ? "var(--lh-bg-1)" : "#f7f7f5",
                backgroundImage: "radial-gradient(ellipse at center, rgba(0,184,219,0.08) 0%, transparent 70%)",
                borderTop: "1px solid rgba(0,184,219,0.15)",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-[56px] items-center"
                style={{
                    maxWidth: "1200px", margin: "0 auto", padding: "72px 24px",
                }}
            >
                {/* ── Left ───────────────────────────────────────────────────── */}
                <div ref={leftRef}>
                    {/* Badge */}
                    <div
                        ref={badgeRef}
                        className="inline-flex items-center gap-2 mb-5"
                        style={{
                            background: isDark ? "rgba(0,184,219,0.08)" : "rgba(0,184,219,0.05)",
                            color: isDark ? "rgba(0,184,219,0.7)" : "#0088a3",
                            fontFamily: "var(--font-mono)", fontSize: "0.6rem",
                            letterSpacing: "0.12em", textTransform: "uppercase",
                            borderRadius: "100px", border: isDark ? "none" : "1px solid rgba(0,184,219,0.25)", padding: "6px 14px", opacity: 0,
                        }}
                    >
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: isDark ? "var(--lh-cyan)" : "#00B8DB", display: "inline-block" }} />
                        Get Started Today
                    </div>

                    {/* Heading */}
                    <div
                        ref={headRef}
                        style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "clamp(2rem, 3.3vw, 2.8rem)", fontWeight: 700,
                            color: isDark ? "var(--lh-text)" : "#111111", lineHeight: 1.15, letterSpacing: "-0.02em",
                            marginBottom: "14px", opacity: 0,
                        }}
                    >
                        Decisions Where<br />You Already Work
                    </div>

                    {/* Para */}
                    <p
                        ref={paraRef}
                        style={{
                            fontSize: "0.86rem", color: isDark ? "rgba(255,255,255,0.65)" : "#4b5563",
                            lineHeight: 1.75, fontStyle: "italic", marginBottom: "24px", opacity: 0,
                        }}
                    >
                        No dashboard sprawl. No context switching. Ask the question you actually have in plain English,
                        inside Claude — and get a decision-ready answer backed by live market intelligence.
                    </p>

                    {/* CTAs */}
                    <div ref={actsRef} className="flex flex-wrap gap-3" style={{ opacity: 0 }}>
                        <a
                            href="#plans"
                            onClick={(e) => handleLandingAnchorClick(e, "#plans")}
                            style={{
                                fontFamily: "var(--font-mono)", fontSize: "0.7rem",
                                padding: "13px 26px", minHeight: "44px", background: "var(--lh-cyan)", color: "#000000",
                                border: "1px solid var(--lh-cyan)", borderRadius: "100px", cursor: "pointer",
                                textDecoration: "none", letterSpacing: "0.06em", fontWeight: 600,
                                transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#19d8ff";
                                e.currentTarget.style.borderColor = "#19d8ff";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "var(--lh-cyan)";
                                e.currentTarget.style.borderColor = "var(--lh-cyan)";
                            }}
                        >
                            Start Free Trial →
                        </a>
                        <a
                            href="#plans"
                            onClick={(e) => handleLandingAnchorClick(e, "#plans")}
                            style={{
                                fontFamily: "var(--font-mono)", fontSize: "0.7rem",
                                padding: "13px 26px", minHeight: "44px", background: "transparent",
                                color: isDark ? "rgba(0,184,219,0.8)" : "rgba(0,184,219,0.9)",
                                border: isDark ? "1px solid rgba(0,184,219,0.35)" : "1px solid rgba(0,184,219,0.5)",
                                borderRadius: "100px", cursor: "pointer",
                                textDecoration: "none", letterSpacing: "0.06em",
                                transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = "rgba(0,184,219,0.7)";
                                e.currentTarget.style.color = isDark ? "var(--lh-cyan)" : "#0088a3";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = isDark ? "rgba(0,184,219,0.35)" : "rgba(0,184,219,0.5)";
                                e.currentTarget.style.color = isDark ? "rgba(0,184,219,0.8)" : "rgba(0,184,219,0.9)";
                            }}
                        >
                            Get Toolbox  $18/mo
                        </a>
                    </div>
                </div>

                {/* ── Right: Candlestick chart ──────────────────────────────── */}
                <div className="flex items-center justify-center">
                    <div
                        ref={candlesRef}
                        className="flex items-end gap-3"
                        style={{
                            padding: "18px",
                            background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                            borderRadius: "16px",
                            border: isDark ? "1px solid rgba(0,184,219,0.1)" : "1px solid #c4c2bc",
                            opacity: 0,
                        }}
                    >
                        {candles.map((c, i) => (
                            <div
                                key={i}
                                ref={(el) => { candleEls.current[i] = el; }}
                                className="flex flex-col items-center gap-1"
                                style={{ opacity: 0 }}
                            >
                                <div className="wick" style={{ width: "1.5px", height: `${c.wickTop}px`, background: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)" }} />
                                <div
                                    className="cbody"
                                    style={{
                                        width: "20px", height: `${c.bodyH}px`,
                                        borderRadius: "3px",
                                        background: c.bull ? (isDark ? "var(--lh-green, #00e5a0)" : "#059669") : (isDark ? "var(--lh-red, #ff4757)" : "#dc2626"),
                                    }}
                                />
                                <div className="wick" style={{ width: "1.5px", height: `${c.wickBot}px`, background: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)" }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
